import React, { useEffect, useState, createContext, useContext } from 'react';
// Special email addresses
const SPECIAL_EMAIL = 'stefan.bira@cautlocuinta.ro';
const RENTAL_CLOSER_EMAIL = 'rentalcloser@gmail.com';
interface UserProfile {
  email: string;
  fullName: string;
  phone: string;
  dateOfBirth: string;
  photo?: string;
  city: string;
  address: string;
  locality: string;
  availability: 'full-time' | 'part-time';
  hoursPerWeek?: number;
  employmentStatus: 'has-job' | 'in-notice' | 'no-job';
  noticeEndDate?: string;
  hasCompany: boolean;
  companyName?: string;
  cui?: string;
  caenCode?: string;
  careerLevel: 'trainee' | 'rental-closer' | 'selling-closer' | 'selling-broker' | 'selling-senior' | 'selling-master';
  points: number;
}
interface AuthContextType {
  user: UserProfile | null;
  isAuthenticated: boolean;
  unlockedAll: boolean;
  isRentalCloser: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateProfile: (profile: Partial<UserProfile>) => void;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
const STEFAN_PROFILE: UserProfile = {
  email: 'stefan.bira@cautlocuinta.ro',
  fullName: 'Stefan Bira',
  phone: '0760243210',
  dateOfBirth: '1993-07-18',
  photo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6rLZaTt9JbqjYqKxMqTZpC/poza_eu_profil.jpg',
  city: 'București',
  address: 'Navodari 20',
  locality: 'sector-1',
  availability: 'full-time',
  employmentStatus: 'no-job',
  hasCompany: true,
  companyName: 'FREIM ESTATE SRL',
  cui: 'RO42451855',
  caenCode: '6831',
  careerLevel: 'trainee',
  points: 0
};
const RENTAL_CLOSER_PROFILE: UserProfile = {
  email: 'rentalcloser@gmail.com',
  fullName: 'Agent Rental Closer',
  phone: '0721000000',
  dateOfBirth: '1990-01-01',
  photo: undefined,
  city: 'București',
  address: 'Str. Exemplu 1',
  locality: 'sector-1',
  availability: 'full-time',
  employmentStatus: 'no-job',
  hasCompany: true,
  companyName: 'RENTAL CLOSER SRL',
  cui: 'RO12345678',
  caenCode: '6831',
  careerLevel: 'rental-closer',
  points: 0
};
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [unlockedAll, setUnlockedAll] = useState(false);
  useEffect(() => {
    const storedUser = localStorage.getItem('clc-user');
    const storedUnlockedAll = localStorage.getItem('clc-unlocked-all');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Error loading user:', e);
      }
    }
    if (storedUnlockedAll === 'true') {
      setUnlockedAll(true);
    }
  }, []);
  const login = async (email: string, password: string) => {
    // Simulate login - in real app this would call an API
    await new Promise(resolve => setTimeout(resolve, 500));
    let userProfile: UserProfile;
    // Check if this is the rental closer email
    if (email.toLowerCase() === RENTAL_CLOSER_EMAIL.toLowerCase()) {
      userProfile = RENTAL_CLOSER_PROFILE;
      setUnlockedAll(false);
      localStorage.removeItem('clc-unlocked-all');
    }
    // Check if this is the special email with pre-populated data
    else if (email.toLowerCase() === SPECIAL_EMAIL.toLowerCase()) {
      userProfile = STEFAN_PROFILE;
      setUnlockedAll(true);
      localStorage.setItem('clc-unlocked-all', 'true');
    } else {
      userProfile = {
        email,
        fullName: '',
        phone: '',
        dateOfBirth: '',
        city: '',
        address: '',
        locality: '',
        availability: 'full-time',
        employmentStatus: 'no-job',
        hasCompany: false,
        careerLevel: 'trainee',
        points: 0
      };
      setUnlockedAll(false);
      localStorage.removeItem('clc-unlocked-all');
    }
    setUser(userProfile);
    localStorage.setItem('clc-user', JSON.stringify(userProfile));
  };
  const logout = () => {
    setUser(null);
    setUnlockedAll(false);
    localStorage.removeItem('clc-user');
    localStorage.removeItem('clc-unlocked-all');
  };
  const updateProfile = (profile: Partial<UserProfile>) => {
    if (!user) return;
    const updatedUser = {
      ...user,
      ...profile
    };
    setUser(updatedUser);
    localStorage.setItem('clc-user', JSON.stringify(updatedUser));
  };
  const isRentalCloser = user?.email.toLowerCase() === RENTAL_CLOSER_EMAIL.toLowerCase();
  return <AuthContext.Provider value={{
    user,
    isAuthenticated: !!user,
    unlockedAll,
    isRentalCloser,
    login,
    logout,
    updateProfile
  }} data-id="element-7101">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}